import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { contaApi } from '../services/api';

export interface ClienteLoja {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  emailVerificado: boolean;
  criadoEm: string;
}

interface ClienteAuthContextType {
  cliente: ClienteLoja | null;
  logado: boolean;
  carregando: boolean;
  login: (email: string, senha: string) => Promise<void>;
  definirSessao: (token: string, cliente: ClienteLoja) => void;
  atualizarCliente: (cliente: ClienteLoja) => void;
  logout: () => void;
}

const ClienteAuthContext = createContext<ClienteAuthContextType | null>(null);

function lerClienteCache(): ClienteLoja | null {
  try {
    const raw = localStorage.getItem('monchery_cliente');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function ClienteAuthProvider({ children }: { children: ReactNode }) {
  const [cliente, setCliente] = useState<ClienteLoja | null>(lerClienteCache);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('monchery_cliente_token');
    if (token) {
      contaApi.me()
        .then(res => {
          setCliente(res.data);
          localStorage.setItem('monchery_cliente', JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem('monchery_cliente_token');
          localStorage.removeItem('monchery_cliente');
          setCliente(null);
        })
        .finally(() => setCarregando(false));
    } else {
      setCarregando(false);
    }
  }, []);

  const definirSessao = (token: string, cliente: ClienteLoja) => {
    localStorage.setItem('monchery_cliente_token', token);
    localStorage.setItem('monchery_cliente', JSON.stringify(cliente));
    setCliente(cliente);
  };

  const login = async (email: string, senha: string) => {
    const res = await contaApi.login(email, senha);
    definirSessao(res.data.token, res.data.cliente);
  };

  const atualizarCliente = (c: ClienteLoja) => {
    localStorage.setItem('monchery_cliente', JSON.stringify(c));
    setCliente(c);
  };

  const logout = () => {
    localStorage.removeItem('monchery_cliente_token');
    localStorage.removeItem('monchery_cliente');
    setCliente(null);
    window.location.href = '/loja/conta/login';
  };

  return (
    <ClienteAuthContext.Provider
      value={{ cliente, logado: !!cliente, carregando, login, definirSessao, atualizarCliente, logout }}
    >
      {children}
    </ClienteAuthContext.Provider>
  );
}

export function useClienteAuth() {
  const context = useContext(ClienteAuthContext);
  if (!context) throw new Error('useClienteAuth deve ser usado dentro de ClienteAuthProvider');
  return context;
}
